import { DroppableZone } from "@/lib/dnd/components/DroppableZone";
import type { HandWritingSlideData } from "@ai-teach/types";
import { IconButton } from "@ai-teach/ui-web";
import { useEffect, useRef, useState } from "react";
import { FaMagic, FaMusic, FaPause, FaPlay } from "react-icons/fa";
import { HiX } from "react-icons/hi";
import { IoMusicalNotes } from "react-icons/io5";

type WordAudio = HandWritingSlideData["textAudios"][number];

interface HandWritingWordAudioProps {
  slideId: string;
  index: number;
  audio: WordAudio | undefined;
  isUploading: boolean;
  canGenerate: boolean;
  onFileDrop: (file: File) => void;
  onGenerate: () => void;
  onBrowsePhonics: () => void;
  onRemove: () => void;
}

/** The clip for one word: dropped from Media House, uploaded locally, generated by TTS, or picked from Phonics. */
export function HandWritingWordAudio({
  slideId,
  index,
  audio,
  isUploading,
  canGenerate,
  onFileDrop,
  onGenerate,
  onBrowsePhonics,
  onRemove,
}: HandWritingWordAudioProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const src = audio?.src ?? null;

  // biome-ignore lint/correctness/useExhaustiveDependencies: a newly picked clip starts paused
  useEffect(() => {
    setIsPlaying(false);
  }, [src]);

  const handlePlayPause = () => {
    const el = audioRef.current;
    if (!el) return;
    if (isPlaying) {
      el.pause();
    } else {
      el.currentTime = 0;
      void el.play();
    }
    setIsPlaying(!isPlaying);
  };

  return (
    <div className="flex items-center gap-2">
      {src && (
        // biome-ignore lint/a11y/useMediaCaption: the clip is the word read aloud
        <audio ref={audioRef} src={src} preload="metadata" onEnded={() => setIsPlaying(false)} />
      )}

      <DroppableZone
        id={`${slideId}-audio-${index}`}
        data={{ id: String(index), type: "handWriting-audio" }}
        allowedType={["audio"]}
        className="flex-1 min-w-0 rounded border"
        defaultBorderClass="border-border"
        onLocalFileDrop={onFileDrop}
        localFileAccept="audio/*"
        clickable={!src}
      >
        <div className="h-8 px-2 flex items-center gap-2">
          {isUploading ? (
            <>
              <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin shrink-0" />
              <p className="text-[11px] text-muted-foreground">Uploading…</p>
            </>
          ) : src ? (
            <>
              <button
                type="button"
                onClick={handlePlayPause}
                aria-label={isPlaying ? "Pause word audio" : "Play word audio"}
                className="shrink-0 w-5 h-5 rounded-full bg-blue-500 hover:bg-blue-600 flex items-center justify-center transition-colors"
              >
                {isPlaying ? (
                  <FaPause className="text-white text-[7px]" />
                ) : (
                  <FaPlay className="text-white text-[7px] ml-0.5" />
                )}
              </button>
              <p className="flex-1 min-w-0 text-[11px] font-medium text-foreground truncate">
                {audio?.filename || "Word audio"}
              </p>
              <IconButton variant="danger" size="sm" onClick={onRemove}>
                <HiX className="text-[10px]" />
              </IconButton>
            </>
          ) : (
            <>
              <FaMusic className="text-muted-foreground text-[10px] shrink-0" />
              <p className="text-[11px] font-medium text-muted-foreground truncate">
                Drop audio or click to upload
              </p>
            </>
          )}
        </div>
      </DroppableZone>

      <button
        type="button"
        onClick={onGenerate}
        disabled={!canGenerate}
        title="Generate the audio from the word with Azure TTS"
        className="shrink-0 h-8 px-2 flex items-center gap-1 text-[10px] font-semibold text-blue-100 bg-blue-900 rounded hover:bg-blue-700 disabled:opacity-40 disabled:hover:bg-blue-900 transition-colors uppercase tracking-widest"
      >
        <FaMagic className="text-[9px]" />
        Generate
      </button>

      <button
        type="button"
        onClick={onBrowsePhonics}
        title="Pick from the Phonics library"
        className="shrink-0 h-8 px-2 flex items-center gap-1 text-[10px] font-semibold text-blue-500 border border-blue-200 rounded hover:bg-blue-50 transition-colors uppercase tracking-widest"
      >
        <IoMusicalNotes className="text-xs" />
        Phonics
      </button>
    </div>
  );
}
